import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const recaptchaRef = useRef();

  const handleChange = (e) => {
    let { name, value } = e.target;

    // ✅ Only allow numeric digits in mobile number
    if (name === "mobile") {
      value = value.replace(/[^0-9]/g, "");
    }

    setForm({ ...form, [name]: value });
    setFieldErrors({ ...fieldErrors, [name]: "" });
  };

  const validateForm = () => {
    const errors = {};

    if (!form.name.trim() || form.name.length < 2) {
      errors.name = "Please enter your full name (min. 2 characters).";
    }

    // ✅ Strict 10 digit validation
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(form.mobile)) {
      errors.mobile = "Enter a valid 10 digit mobile number.";
    }

    if (!/\S+@\S+\.\S+/.test(form.email)) {
      errors.email = "Please enter a valid email address.";
    }

    const message = form.message.trim();
    if (message.length < 5 || message.length > 2000) {
      errors.message = "Message must be between 5 and 2000 characters.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateForm()) return;

    try {
      setLoading(true);

      const token = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();

      const payload = {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        token,
      };

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/enquiry`, payload);

      setSubmitted(true);
    } catch (err) {
      console.error("❌ Error submitting enquiry:", err);
      if (err.response) {
        setError(err.response.data?.error || "Something went wrong. Please try again later.");
      } else {
        setError("Network error. Please check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-xl mx-auto text-center bg-green-50 dark:bg-green-900/40 p-8 rounded-2xl shadow-lg mt-8"
      >
        <h2 className="text-2xl font-bold text-green-700 dark:text-green-300">✅ Enquiry Submitted</h2>
        <p className="mt-4 text-green-600 dark:text-green-400">
          Thank you for reaching out! Our team will review your enquiry and get back to you within 24 hours.
        </p>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-3xl mx-auto bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl space-y-6"
    >
      <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-white mb-4">
        📩 Contact Us
      </h2>

      {/* Full Name */}
      <div>
        <input
          type="text"
          name="name"
          placeholder="Full Name"
          value={form.name}
          onChange={handleChange}
          disabled={loading}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
            fieldErrors.name ? "border-red-500" : "focus:ring-yellow-400"
          } dark:bg-gray-700 dark:text-white`}
        />
        {fieldErrors.name && <p className="text-red-500 text-sm mt-1">{fieldErrors.name}</p>}
      </div>

      {/* ✅ Mobile Number (Strict 10-digit input) */}
      <div>
        <input
          type="text"
          name="mobile"
          placeholder="Enter 10 digit mobile number"
          maxLength={10}
          value={form.mobile}
          onChange={handleChange}
          disabled={loading}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
            fieldErrors.mobile ? "border-red-500" : "focus:ring-yellow-400"
          } dark:bg-gray-700 dark:text-white`}
        />
        {fieldErrors.mobile && <p className="text-red-500 text-sm mt-1">{fieldErrors.mobile}</p>}
      </div>

      {/* Email */}
      <div>
        <input
          type="email"
          name="email"
          placeholder="Email Address"
          value={form.email}
          onChange={handleChange}
          disabled={loading}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
            fieldErrors.email ? "border-red-500" : "focus:ring-yellow-400"
          } dark:bg-gray-700 dark:text-white`}
        />
        {fieldErrors.email && <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>}
      </div>

      {/* Message */}
      <div>
        <textarea
          name="message"
          placeholder="Your Enquiry"
          rows="5"
          value={form.message}
          onChange={handleChange}
          disabled={loading}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 ${
            fieldErrors.message ? "border-red-500" : "focus:ring-yellow-400"
          } dark:bg-gray-700 dark:text-white`}
        />
        {fieldErrors.message && <p className="text-red-500 text-sm mt-1">{fieldErrors.message}</p>}
      </div>

      <ReCAPTCHA sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} size="invisible" ref={recaptchaRef} />

      {error && <p className="text-red-600 font-semibold text-center">{error}</p>}

      <motion.button
        whileHover={{ scale: loading ? 1 : 1.05 }}
        whileTap={{ scale: loading ? 1 : 0.95 }}
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-gradient-to-r from-yellow-400 to-red-400 text-gray-900 font-bold rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {loading ? "Submitting..." : "Submit Enquiry"}
      </motion.button>
    </form>
  );
};

export default ContactForm;
